
import React, { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { FactoryList } from "@/components/factories/FactoryList";
import { FactoryDialog } from "@/components/factories/FactoryDialog";
import { useFactories, Factory } from "@/hooks/useFactories";
import { FactoryFormValues } from "@/components/factories/FactoryForm";
import { SearchBar } from "@/components/shared/SearchBar";
import { Button } from "@/components/ui/button";
import { PlusCircle } from "lucide-react";

const Factories = () => {
  const [openDialog, setOpenDialog] = useState(false);
  const [editingFactory, setEditingFactory] = useState<Factory | null>(null);
  const [factoriesList, setFactoriesList] = useState<Factory[]>([]);
  const [filteredFactories, setFilteredFactories] = useState<Factory[]>([]);
  
  const { 
    factories, 
    loading, 
    fetchFactories, 
    createFactory, 
    updateFactory, 
    deleteFactory 
  } = useFactories();

  // Update local state when factories data changes
  useEffect(() => {
    if (factories) {
      setFactoriesList(factories);
      setFilteredFactories(factories);
    }
  }, [factories]);

  const handleSearch = (query: string) => {
    if (!query) {
      setFilteredFactories(factoriesList);
      return;
    }

    const normalizedQuery = query.toLowerCase();
    const filtered = factoriesList.filter(factory => {
      return (
        factory.name.toLowerCase().includes(normalizedQuery) ||
        (factory.factory_number && factory.factory_number.toLowerCase().includes(normalizedQuery)) ||
        (factory.location && factory.location.toLowerCase().includes(normalizedQuery)) ||
        (factory.contact_person && factory.contact_person.toLowerCase().includes(normalizedQuery)) ||
        (factory.email && factory.email.toLowerCase().includes(normalizedQuery)) ||
        (factory.phone && factory.phone.toLowerCase().includes(normalizedQuery))
      );
    });
    setFilteredFactories(filtered);
  };

  const handleSubmit = async (values: FactoryFormValues) => {
    try {
      let success;
      
      if (editingFactory) {
        success = await updateFactory(editingFactory.id, values);
      } else {
        success = await createFactory(values);
      }

      if (success) {
        setOpenDialog(false);
        setEditingFactory(null);
        fetchFactories();
      }
    } catch (error: any) {
      // Error is handled in the hook
    }
  };

  const handleEdit = (factory: Factory) => {
    setEditingFactory(factory);
    setOpenDialog(true);
  };

  const handleAddNew = () => {
    setEditingFactory(null);
    setOpenDialog(true);
  };

  const handleDelete = async (factory: Factory) => {
    const success = await deleteFactory(factory);
    if (success) {
      // Immediately update UI by filtering out the deleted factory
      const updated = factoriesList.filter(f => f.id !== factory.id);
      setFactoriesList(updated);
      setFilteredFactories(updated);
    }
    return success;
  };

  const defaultValues: FactoryFormValues = {
    name: editingFactory?.name || "",
    factory_number: editingFactory?.factory_number || "",
    location: editingFactory?.location || "",
    contact_person: editingFactory?.contact_person || "",
    phone: editingFactory?.phone || "",
    email: editingFactory?.email || "",
    active: editingFactory?.active ?? true,
    capacity: editingFactory?.capacity || null,
  };

  return (
    <div className="space-y-8">
      <Card className="glass-card p-6 animate-fadeIn">
        <h2 className="text-2xl font-semibold mb-4">Factory & Stitching Services</h2>
        <p className="text-muted-foreground">
          Manage factories and production facilities.
        </p>
      </Card>

      <Card className="p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium">Factories</h3>
          <div className="flex items-center gap-2">
            <SearchBar 
              placeholder="Search factories..."
              onSearch={handleSearch}
              className="w-auto"
            />
            <Button onClick={handleAddNew} className="flex items-center gap-2">
              <PlusCircle className="h-4 w-4" />
              Add Factory
            </Button>
          </div>
        </div>
        <FactoryList
          factories={filteredFactories}
          isLoading={loading}
          onAdd={handleAddNew}
          onEdit={handleEdit}
          onDelete={handleDelete}
        />
      </Card>

      <FactoryDialog
        open={openDialog}
        onOpenChange={setOpenDialog}
        onSubmit={handleSubmit}
        defaultValues={defaultValues}
        isEditing={!!editingFactory}
      />
    </div>
  );
};

export default Factories;
